import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IRestrictedArea, IRestrictedAreaInput, IRestrictedAreaOptions } from '@d-orca/shared/alert-kinds';
import { professionOptionLabel } from '@d-orca/shared/client/utils';
import { Button } from '@components/ui/Button/Button';
import { CheckListPick, type ICheckListOption } from '@components/ui/CheckListPick/CheckListPick';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { Modal } from '@components/ui/Modal/Modal';
import { canSubmit, draftFrom, isChanged, toggleId, togglePick, type AllOrIds, optionsFor } from './restrictedAreaDraft';
import styles from './RestrictedAreaDialog.module.css';

const K = 'alertSettings.restrictedArea.dialog';
const FLOOR = 'floor:';
const AREA = 'area:';

export interface IRestrictedAreaDialogProps {
  /** `null`: create a new area. */
  area: IRestrictedArea | null;
  options: IRestrictedAreaOptions;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: IRestrictedAreaInput) => void;
  /** Edit only — after the reader confirmed. */
  onDelete: () => void;
}

const labelsOf = (options: ICheckListOption[], selected: ReadonlySet<string>) =>
  options.filter((o) => selected.has(o.value)).map((o) => o.label).join(', ');

interface IAllOrIdsFieldProps {
  label: string;
  allLabel: string;
  placeholder: string;
  options: ICheckListOption[];
  value: AllOrIds;
  onChange: (next: AllOrIds) => void;
  noMatches: string;
}

/** A pick with an "All …" choice above its rows — professions and certificates. */
function AllOrIdsField({ label, allLabel, placeholder, options, value, onChange, noMatches }: IAllOrIdsFieldProps) {
  const allIds = options.map((o) => o.value);
  const selected = new Set(value === 'all' ? allIds : value);
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <CheckListPick
        summary={value === 'all' ? allLabel : labelsOf(options, selected)}
        placeholder={placeholder}
        ariaLabel={label}
        options={options}
        selected={selected}
        onToggle={(id, next) => onChange(togglePick(value, allIds, id, next))}
        all={{ label: allLabel, checked: value === 'all', onToggle: (next) => onChange(next ? 'all' : []) }}
        noMatches={noMatches}
      />
    </div>
  );
}

/**
 * Create or edit one restricted area: its place (floors and areas), the
 * professions it is closed to, and the certificates that let a worker in
 * anyway. The professions and certificates fields show only when the project
 * has any. Edit also deletes, after a confirmation.
 */
export function RestrictedAreaDialog({ area, options: projectOptions, submitting, onClose, onSubmit, onDelete }: IRestrictedAreaDialogProps) {
  const { t } = useTranslation();
  const [initial] = useState(() => draftFrom(area));
  const options = optionsFor(projectOptions, area);
  const [draft, setDraft] = useState(initial);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const isEdit = area !== null;
  const noMatches = t(`${K}.noMatches`);

  const floorNames = new Map(options.floors.map((f) => [f.id, f.name]));
  const locationOptions: ICheckListOption[] = [
    ...options.floors.map((f) => ({ value: FLOOR + f.id, label: f.name })),
    ...options.areas.map((a) => ({ value: AREA + a.id, label: a.name, hint: floorNames.get(a.floorId) })),
  ];
  const selectedLocations = new Set([...draft.floorIds.map((id) => FLOOR + id), ...draft.areaIds.map((id) => AREA + id)]);
  const toggleLocation = (value: string, next: boolean) =>
    setDraft((d) =>
      value.startsWith(FLOOR)
        ? { ...d, floorIds: toggleId(d.floorIds, value.slice(FLOOR.length), next) }
        : { ...d, areaIds: toggleId(d.areaIds, value.slice(AREA.length), next) },
    );

  const enabled = canSubmit(draft) && (!isEdit || isChanged(draft, initial)) && !submitting;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={t(isEdit ? `${K}.editTitle` : `${K}.createTitle`)}
        footer={
          <>
            {isEdit && (
              <Button variant="ghost" size="md" disabled={submitting} onClick={() => setConfirmingDelete(true)}>
                {t('common.delete')}
              </Button>
            )}
            <span className={styles.spacer} />
            <Button variant="secondary" size="md" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" size="md" disabled={!enabled} onClick={() => onSubmit(draft)}>
              {t(isEdit ? 'common.save' : 'common.create')}
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          <div className={styles.field}>
            <span className={styles.label}>{t(`${K}.location`)}</span>
            <CheckListPick
              summary={labelsOf(locationOptions, selectedLocations)}
              placeholder={t(`${K}.locationPlaceholder`)}
              ariaLabel={t(`${K}.location`)}
              options={locationOptions}
              selected={selectedLocations}
              onToggle={toggleLocation}
              searchPlaceholder={t(`${K}.search`)}
              noMatches={noMatches}
            />
          </div>
          {options.professions.length > 0 && (
            <AllOrIdsField
              label={t(`${K}.restrictedFor`)}
              allLabel={t('alertSettings.restrictedArea.allProfessions')}
              placeholder={t(`${K}.professionsPlaceholder`)}
              options={options.professions.map((p) => ({ value: p.id, label: professionOptionLabel(t, p) }))}
              value={draft.professionIds}
              onChange={(professionIds) => setDraft((d) => ({ ...d, professionIds }))}
              noMatches={noMatches}
            />
          )}
          {options.certificates.length > 0 && (
            <AllOrIdsField
              label={t(`${K}.permittedWith`)}
              allLabel={t('alertSettings.restrictedArea.allCertificates')}
              placeholder={t(`${K}.certificatesPlaceholder`)}
              options={options.certificates.map((c) => ({ value: c.id, label: c.name }))}
              value={draft.certificateIds}
              onChange={(certificateIds) => setDraft((d) => ({ ...d, certificateIds }))}
              noMatches={noMatches}
            />
          )}
        </div>
      </Modal>
      <DiscardModal
        open={confirmingDelete}
        title={t(`${K}.deleteTitle`)}
        description={t(`${K}.deleteBody`)}
        loading={submitting}
        onConfirm={onDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
}
